import { Component, ElementRef, HostListener, Inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface ImagePreviewDialogData {
  url: string;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const BUTTON_SCALE_STEP = 0.25;
const WHEEL_SCALE_STEP = 0.1;
const DOUBLE_CLICK_SCALE = 2;

interface Point {
  x: number;
  y: number;
}

@Component({
  selector: 'app-image-preview-dialog',
  templateUrl: './image-preview-dialog.component.html',
  styleUrls: ['./image-preview-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ImagePreviewDialogComponent {
  @ViewChild('imageEl') imageEl?: ElementRef<HTMLImageElement>;

  scale = 1;
  translateX = 0;
  translateY = 0;

  private isPanning = false;
  private panStart: Point = { x: 0, y: 0 };
  private panOrigin: Point = { x: 0, y: 0 };

  // Tracks the pinch gesture's starting distance/scale so each touchmove only applies the delta.
  private pinchStartDistance = 0;
  private pinchStartScale = 1;

  constructor(
    public dialogRef: MatDialogRef<ImagePreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImagePreviewDialogData,
  ) {}

  get zoomPercent(): number {
    return Math.round(this.scale * 100);
  }

  get canZoomIn(): boolean {
    return this.scale < MAX_SCALE - 0.001;
  }

  get canZoomOut(): boolean {
    return this.scale > MIN_SCALE + 0.001;
  }

  get isZoomed(): boolean {
    return this.scale > MIN_SCALE;
  }

  get imageTransform(): string {
    return `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }

  close(): void {
    this.dialogRef.close();
  }

  zoomIn(): void {
    this.setScale(this.scale + BUTTON_SCALE_STEP);
  }

  zoomOut(): void {
    this.setScale(this.scale - BUTTON_SCALE_STEP);
  }

  resetZoom(): void {
    this.scale = MIN_SCALE;
    this.translateX = 0;
    this.translateY = 0;
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -WHEEL_SCALE_STEP : WHEEL_SCALE_STEP;
    this.setScale(this.scale + delta);
  }

  onDoubleClick(): void {
    if (this.isZoomed) {
      this.resetZoom();
    } else {
      this.setScale(DOUBLE_CLICK_SCALE);
    }
  }

  onPanStart(event: MouseEvent): void {
    if (!this.isZoomed) {
      return;
    }
    event.preventDefault();
    this.isPanning = true;
    this.panStart = { x: event.clientX, y: event.clientY };
    this.panOrigin = { x: this.translateX, y: this.translateY };
  }

  @HostListener('document:mousemove', ['$event'])
  onPanMove(event: MouseEvent): void {
    if (!this.isPanning) {
      return;
    }
    this.translateX = this.panOrigin.x + (event.clientX - this.panStart.x);
    this.translateY = this.panOrigin.y + (event.clientY - this.panStart.y);
  }

  @HostListener('document:mouseup')
  onPanEnd(): void {
    this.isPanning = false;
  }

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 2) {
      event.preventDefault();
      this.isPanning = false;
      this.pinchStartDistance = this.touchDistance(event.touches);
      this.pinchStartScale = this.scale;
    } else if (event.touches.length === 1 && this.isZoomed) {
      event.preventDefault();
      this.isPanning = true;
      const touch = event.touches[0];
      this.panStart = { x: touch.clientX, y: touch.clientY };
      this.panOrigin = { x: this.translateX, y: this.translateY };
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (event.touches.length === 2 && this.pinchStartDistance > 0) {
      event.preventDefault();
      const distance = this.touchDistance(event.touches);
      const factor = distance / this.pinchStartDistance;
      this.setScale(this.pinchStartScale * factor);
    } else if (event.touches.length === 1 && this.isPanning) {
      event.preventDefault();
      const touch = event.touches[0];
      this.translateX = this.panOrigin.x + (touch.clientX - this.panStart.x);
      this.translateY = this.panOrigin.y + (touch.clientY - this.panStart.y);
    }
  }

  onTouchEnd(event: TouchEvent): void {
    if (event.touches.length < 2) {
      this.pinchStartDistance = 0;
    }
    if (event.touches.length === 1 && this.isZoomed) {
      // Re-anchor on the remaining finger so a pinch can hand off into a pan without lifting fully.
      const touch = event.touches[0];
      this.isPanning = true;
      this.panStart = { x: touch.clientX, y: touch.clientY };
      this.panOrigin = { x: this.translateX, y: this.translateY };
    } else if (event.touches.length === 0) {
      this.isPanning = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
      case '_':
        this.zoomOut();
        break;
      case '0':
        this.resetZoom();
        break;
      default:
        break;
    }
  }

  private touchDistance(touches: TouchList): number {
    const [a, b] = [touches[0], touches[1]];
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  }

  private setScale(newScale: number): void {
    const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale));
    this.scale = Math.round(clamped * 100) / 100;
    if (this.scale <= MIN_SCALE) {
      this.translateX = 0;
      this.translateY = 0;
    }
  }
}
